/**
 * 新版课程中心「基础测试」：仅支持接口 `pre_questions: { type, list }`。
 *
 * @module views/Course/utils/foundationTest
 */

/**
 * @typedef {'QA'|'Attachment'|'MCQ'} FoundationTestType
 */

/**
 * @typedef {Object} FoundationTestListItem
 * @property {string} [q] - 题干文本
 * @property {string} [a] - 参考答案文本
 * @property {string} [q_img] - 题干图片 URL
 * @property {string} [a_img] - 参考答案图片 URL
 * @property {Array} [options] - MCQ 选项
 */

/**
 * @param {*} val
 * @returns {string}
 */
export function trimFoundationField(val) {
  if (val === null || val === undefined) {
    return '';
  }
  return String(val).trim();
}

/**
 * 规范化单题展示字段（文本 / 图片分字段，空串视为无内容）。
 *
 * @param {FoundationTestListItem} [item]
 * @returns {{ questionText: string, questionImage: string, answerText: string, answerImage: string, options?: Array }}
 */
export function normalizeFoundationTestListItem(item) {
  return {
    questionText: trimFoundationField(item?.q),
    questionImage: trimFoundationField(item?.q_img),
    answerText: trimFoundationField(item?.a),
    answerImage: trimFoundationField(item?.a_img),
    options: item?.options
  };
}

/**
 * 解析基础测试元数据（非 `{ type, list }` 或空 list 视为无测试）。
 *
 * @param {Object} [course] - 课程
 * @returns {{ type: FoundationTestType|string|null, list: Array<Object> }}
 */
export function getFoundationTestMeta(course) {
  const pq = course && course.pre_questions;
  if (pq && typeof pq === 'object' && !Array.isArray(pq) && Array.isArray(pq.list) && pq.list.length > 0) {
    return { type: pq.type || null, list: pq.list };
  }
  return { type: null, list: [] };
}

/**
 * @param {Object} [course] - 课程
 * @returns {boolean}
 */
export function hasFoundationTest(course) {
  return getFoundationTestMeta(course).list.length > 0;
}

/**
 * @param {Object} [course] - 课程
 * @returns {FoundationTestType|string|null}
 */
export function getFoundationTestType(course) {
  return getFoundationTestMeta(course).type;
}

/**
 * 与后端约定的表单 task id（仅新版 Course 使用）。
 *
 * @param {string|number} courseId - 课程 id
 * @returns {string}
 */
export function foundationTestFormName(courseId) {
  return `CourseFoundationTest_${courseId}`;
}

/**
 * 将 MCQ 的 `options` 规范为 RadioGroup 所需的 `{ label, value }[]`。
 * 接口约定：`label` ← `text`，`value` ← `val`；纯字符串则 label/value 相同。
 *
 * @param {Array<string|{text?:string,val?:*}>} [options] - 原始选项
 * @returns {Array<{label:string,value:string}>}
 */
export function normalizeMcqRadioOptions(options) {
  if (!Array.isArray(options) || !options.length) {
    return [];
  }
  return options
    .map((opt, index) => {
      if (typeof opt === 'string') {
        return { label: opt, value: opt };
      }
      if (!opt || typeof opt !== 'object') {
        return null;
      }
      const value =
        opt.val !== null && opt.val !== undefined ? String(opt.val) : String(opt.text ?? index);
      const label = String(opt.text ?? value);
      return { label, value };
    })
    .filter(row => row !== null && row !== undefined);
}
